import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../shared/SEO';
import { useSettings } from '../../providers/SettingsContext';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const PERSONALIZATION_OPTIONS = [
  {
    key: 'products',
    icon: 'fa-shirt',
    eyebrow: 'Tu diseño listo',
    title: 'Personalizá nuestros productos',
    description: '¿Ya tenés tu logo, foto, nombre o diseño? Elegí un producto y contanos cómo querés personalizarlo.',
    tags: ['Remeras', 'Canguros', 'Gorras', 'Bolsas', 'Lanyards', 'Almohadones', '+ más'],
    cta: 'Ver productos personalizados',
    to: '/tienda?category=Personalizados',
    image: '/images/customization/tshirt-front-white-polyester.webp',
    imageAlt: 'Remera blanca personalizable'
  },
  {
    key: 'ai',
    icon: 'fa-wand-magic-sparkles',
    eyebrow: 'Una idea nueva',
    title: 'Crealo con Inteligencia Artificial',
    description: 'Subí una foto, agregá un nombre o describí una idea. Nuestra IA crea el diseño y después podés convertirlo en un producto real.',
    tags: ['Fotos', 'Nombres', 'Diseños únicos', 'Regalos'],
    cta: 'Crear con IA',
    to: '/personalizados/ia',
    image: '/images/categories/personalizados.png',
    imageAlt: 'Productos personalizados creados con inteligencia artificial'
  },
  {
    key: 'merchandising',
    icon: 'fa-building',
    eyebrow: 'Para grupos y marcas',
    title: 'Merchandising para tu marca',
    description: 'Tenemos una propuesta para vos: una amplia gama de estampados y productos personalizados, tanto en textiles como en superficies rígidas, para empresas, equipos y eventos.',
    tags: ['Empresas', 'Eventos', 'Equipos', 'Pedidos por cantidad'],
    cta: 'Ver Merchandising',
    to: '/merchandising',
    image: '/images/customization/hoodie-front-neutral.webp',
    imageAlt: 'Indumentaria para merchandising empresarial'
  }
];

const DECISION_GUIDE = [
  {
    icon: 'fa-image',
    text: 'Tengo mi logo, foto o diseño',
    label: 'Productos Personalizados',
    to: '/tienda?category=Personalizados'
  },
  {
    icon: 'fa-lightbulb',
    text: 'Quiero crear un diseño nuevo con una foto, nombre o idea',
    label: 'Personalizados IA',
    to: '/personalizados/ia'
  },
  {
    icon: 'fa-boxes-stacked',
    text: 'Necesito varias unidades para una empresa, equipo o evento',
    label: 'Merchandising',
    to: '/merchandising'
  }
];

const CUSTOMIZABLE_PRODUCTS = [
  ['Remeras', 'fa-shirt'],
  ['Canguros', 'fa-shirt'],
  ['Cuadros', 'fa-image'],
  ['Almohadones', 'fa-couch'],
  ['Gorras', 'fa-hat-cowboy'],
  ['Lanyards', 'fa-id-badge'],
  ['Bolsas', 'fa-bag-shopping'],
  ['Delantales', 'fa-user-tie'],
  ['Mochilas', 'fa-school'],
  ['Luncheras', 'fa-box'],
  ['Remeras Dry Fit', 'fa-person-running'],
  ['Próximamente más productos', 'fa-plus']
];

const PersonalizadosLanding = () => {
  const { settings } = useSettings();
  const configuredImages = settings?.homeImages?.personalizados || {};
  const personalizationOptions = PERSONALIZATION_OPTIONS.map(option => ({
    ...option,
    image: getDisplayImageUrl(configuredImages[option.key] || option.image)
  }));

  return (
    <>
      <SEO
      title="Productos Personalizados, IA y Merchandising"
      description="Personalizá productos con tu diseño, creá una idea con inteligencia artificial o solicitá merchandising para tu empresa, equipo o evento."
      url="/personalizados"
      type="website"
      breadcrumbs={[
        { name: 'Inicio', url: '/' },
        { name: 'Personalizados', url: '/personalizados' }
      ]}
    />

    <main className="personalizados-landing">
      <section className="personalizados-hero">
        <div className="personalizados-hero-glow personalizados-hero-glow-one" aria-hidden="true"></div>
        <div className="personalizados-hero-glow personalizados-hero-glow-two" aria-hidden="true"></div>
        <div className="personalizados-shell personalizados-hero-content">
          <span className="personalizados-kicker"><i className="fa-solid fa-pen-ruler"></i> Hecho para vos</span>
          <h1>Personalizá a tu manera</h1>
          <p><strong>Tu idea. Tu diseño. Tu producto.</strong><span>Elegí cómo querés empezar y nosotros hacemos el resto.</span></p>
          <a href="#opciones-personalizados" className="personalizados-hero-link">
            Elegir una opción <i className="fa-solid fa-arrow-down"></i>
          </a>
        </div>
      </section>

      <section id="opciones-personalizados" className="personalizados-section personalizados-options-section">
        <div className="personalizados-shell">
          <div className="personalizados-section-heading">
            <span>Empezá por acá</span>
            <h2>Tres formas de convertir tu idea en algo real</h2>
            <p>Elegí la opción que mejor se adapte a lo que necesitás.</p>
          </div>

          <div className="personalizados-options-grid">
            {personalizationOptions.map(option => (
              <article key={option.key} className={`personalizados-option-card personalizados-option-${option.key}`}>
                <div className="personalizados-option-image">
                  <img src={option.image} alt={option.imageAlt} />
                  <span><i className={`fa-solid ${option.icon}`}></i>{option.eyebrow}</span>
                </div>
                <div className="personalizados-option-content">
                  <h3>{option.title}</h3>
                  <p>{option.description}</p>
                  <div className="personalizados-tags" aria-label={`Opciones de ${option.title}`}>
                    {option.tags.map(tag => <span key={tag}>{tag}</span>)}
                  </div>
                  <Link to={option.to} className="personalizados-card-cta">
                    {option.cta}<i className="fa-solid fa-arrow-right"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="personalizados-section personalizados-guide-section">
        <div className="personalizados-shell">
          <div className="personalizados-section-heading">
            <span>Guía rápida</span>
            <h2>¿No sabés cuál elegir?</h2>
          </div>
          <div className="personalizados-guide-grid">
            {DECISION_GUIDE.map(item => (
              <Link key={item.label} to={item.to} className="personalizados-guide-card">
                <span className="personalizados-guide-icon"><i className={`fa-solid ${item.icon}`}></i></span>
                <span className="personalizados-guide-copy"><small>{item.text}</small><strong>{item.label}</strong></span>
                <i className="fa-solid fa-arrow-right personalizados-guide-arrow"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="personalizados-section personalizados-products-section">
        <div className="personalizados-shell">
          <div className="personalizados-section-heading">
            <span>Muchas posibilidades</span>
            <h2>Todo lo que podemos personalizar</h2>
            <p>Desde una unidad especial hasta un pedido para todo tu equipo.</p>
          </div>
          <div className="personalizados-products-grid">
            {CUSTOMIZABLE_PRODUCTS.map(([name, icon]) => (
              <div key={name} className="personalizados-product-item">
                <span><i className={`fa-solid ${icon}`}></i></span>
                <strong>{name}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  </>
  );
};

export default PersonalizadosLanding;
